/**
 * Format numbers for display in the UI
 * - < 1,000 → 2 decimals (e.g., 487.30)
 * - ≥ 1,000 → compact format (e.g., 12.4K, 1.02M)
 */
export function formatNumber(value: number | null | undefined): string {
  // Handle null, undefined, or non-numeric values
  if (value == null || typeof value !== 'number' || !Number.isFinite(value)) {
    return '0.00';
  }

  if (value < 1000) {
    return value.toFixed(2);
  }

  if (value < 1000000) {
    return `${(value / 1000).toFixed(2).replace(/\.00$/, '')}K`;
  }

  return `${(value / 1000000).toFixed(2).replace(/\.00$/, '')}M`;
}

/**
 * Format currency values for display using Intl.NumberFormat
 * - < 1,000 → €487.30
 * - ≥ 1,000 → €12.4K, €1.02M
 */
export function formatCurrency(value: number | null | undefined, currency: string = 'EUR'): string {
  // Handle null, undefined, or non-numeric values
  if (value == null || typeof value !== 'number' || !Number.isFinite(value)) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatter.format(0);
  }

  // Get currency symbol using Intl.NumberFormat
  const getCurrencySymbol = (curr: string): string => {
    try {
      const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: curr })
        .format(0)
        .replace(/[\d.,\s]/g, '');
      return formatted || '€';
    } catch {
      return '€';
    }
  };

  const symbol = getCurrencySymbol(currency);

  // For values >= 1000, use compact K/M notation with currency symbol
  if (value >= 1000) {
    if (value >= 1000000) {
      const millions = value / 1000000;
      const formatted =
        millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(2).replace(/\.00$/, '');
      return `${symbol}${formatted}M`;
    }
    const thousands = value / 1000;
    const formatted =
      thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(2).replace(/\.00$/, '');
    return `${symbol}${formatted}K`;
  }

  // For values < 1000, use standard currency formatting with 2 decimals
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(value).replace(currency, symbol);
}

/**
 * Format integer values (no decimals)
 * - < 1,000 → 487
 * - ≥ 1,000 → 12.4K, 1.02M
 */
export function formatInteger(value: number | null | undefined): string {
  // Handle null, undefined, or non-numeric values
  if (value == null || typeof value !== 'number' || !Number.isFinite(value)) {
    return '0';
  }

  if (value < 1000) {
    return value.toLocaleString();
  }

  if (value < 1000000) {
    const k = value / 1000;
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(1)}K`;
  }

  const m = value / 1000000;
  return m % 1 === 0 ? `${m}M` : `${m.toFixed(2)}M`;
}

/**
 * Format percentage values
 */
export function formatPercentage(value: number | null | undefined, decimals: number = 2): string {
  // Handle null, undefined, or non-numeric values
  if (value == null || typeof value !== 'number' || !Number.isFinite(value)) {
    return '0.00%';
  }
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format an ISO timestamp as a short relative time ("Just now", "5m ago", "3h ago", "2d ago").
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';

  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Get the full precision value for display in detail view
 * Uses Intl.NumberFormat for proper locale-aware formatting
 */
export function formatFullPrecision(value: number | null | undefined, currency?: string): string {
  // Handle null, undefined, or non-numeric values
  if (value == null || typeof value !== 'number' || !Number.isFinite(value)) {
    if (currency) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return formatter.format(0);
    }
    return '0.00';
  }

  if (currency) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    });
    return formatter.format(value);
  }

  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}
